import { useMemo, useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mockLeads } from '@/mocks/leads';
import { mockPartners } from '@/mocks/partners';
import { mockGeos } from '@/mocks/geos';
import { mockOffers } from '@/mocks/offers';
import { mockPayoutLines } from '@/mocks/payouts';
import { mockDisputes } from '@/mocks/disputes';
import { mockVerticals } from '@/mocks/verticals';
import { useLoading } from '@/hooks/useLoading';
import { SkeletonStat, SkeletonRow } from '@/components/Skeleton';
import {
  TrendingUp,
  TrendingDown,
  Inbox,
  CheckCircle,
  Route,
  FileSignature,
  Euro,
  AlertTriangle,
  Activity,
  Users,
  Clock,
  ArrowUpRight,
  Sparkles,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Calendar,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import BentoCard from '@/components/BentoCard';

interface StatTile {
  label: string;
  value: string;
  delta: string;
  deltaPositive: boolean;
  icon: React.ReactNode;
}

export default function DashboardPage() {
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const thisYear = now.getFullYear();
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const monthLeads = mockLeads.filter(l => {
      const d = new Date(l.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });
    const lastMonthLeads = mockLeads.filter(l => {
      const d = new Date(l.created_at);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    const validated = monthLeads.filter(l => l.validation_status === 'valid');
    const lastValidated = lastMonthLeads.filter(l => l.validation_status === 'valid');
    const routed = monthLeads.filter(l => l.routing_status === 'assigned');
    const lastRouted = lastMonthLeads.filter(l => l.routing_status === 'assigned');
    const signed = monthLeads.filter(l => l.status === 'signed');
    const lastSigned = lastMonthLeads.filter(l => l.status === 'signed');

    const payout = mockPayoutLines
      .filter(p => {
        const d = new Date(p.created_at);
        return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
      })
      .reduce((sum, p) => sum + p.amount_eur, 0);
    const lastPayout = mockPayoutLines
      .filter(p => {
        const d = new Date(p.created_at);
        return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
      })
      .reduce((sum, p) => sum + p.amount_eur, 0);

    const unmatched = mockLeads.filter(l => l.routing_status === 'unmatched' && !l.deleted_at);

    const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);
    const deltaPct = (curr: number, prev: number) =>
      prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0;

    const tiles: StatTile[] = [
      {
        label: 'Leads this month',
        value: String(monthLeads.length),
        delta: `${deltaPct(monthLeads.length, lastMonthLeads.length) >= 0 ? '+' : ''}${deltaPct(monthLeads.length, lastMonthLeads.length)}%`,
        deltaPositive: deltaPct(monthLeads.length, lastMonthLeads.length) >= 0,
        icon: <Inbox size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Validated rate',
        value: `${pct(validated.length, monthLeads.length)}%`,
        delta: `${deltaPct(pct(validated.length, monthLeads.length), pct(lastValidated.length, lastMonthLeads.length)) >= 0 ? '+' : ''}${deltaPct(pct(validated.length, monthLeads.length), pct(lastValidated.length, lastMonthLeads.length))}%`,
        deltaPositive: pct(validated.length, monthLeads.length) >= pct(lastValidated.length, lastMonthLeads.length),
        icon: <CheckCircle size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Routed rate',
        value: `${pct(routed.length, validated.length)}%`,
        delta: `${deltaPct(pct(routed.length, validated.length), pct(lastRouted.length, lastValidated.length)) >= 0 ? '+' : ''}${deltaPct(pct(routed.length, validated.length), pct(lastRouted.length, lastValidated.length))}%`,
        deltaPositive: pct(routed.length, validated.length) >= pct(lastRouted.length, lastValidated.length),
        icon: <Route size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Leads signed',
        value: String(signed.length),
        delta: `${deltaPct(signed.length, lastSigned.length) >= 0 ? '+' : ''}${deltaPct(signed.length, lastSigned.length)}%`,
        deltaPositive: deltaPct(signed.length, lastSigned.length) >= 0,
        icon: <FileSignature size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Gross payout',
        value: `€${payout.toLocaleString()}`,
        delta: `${deltaPct(payout, lastPayout) >= 0 ? '+' : ''}${deltaPct(payout, lastPayout)}%`,
        deltaPositive: deltaPct(payout, lastPayout) >= 0,
        icon: <Euro size={18} strokeWidth={1.5} />,
      },
      {
        label: 'Unmatched',
        value: String(unmatched.length),
        delta: unmatched.length > 0 ? 'Needs attention' : 'All clear',
        deltaPositive: unmatched.length === 0,
        icon: <AlertTriangle size={18} strokeWidth={1.5} />,
      },
    ];

    return tiles;
  }, []);

  const inboxLeads = useMemo(() => {
    // Leads that need operator attention: unmatched + new leads from last 24h
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const needsAction = mockLeads
      .filter(l => !l.deleted_at && (l.routing_status === 'unmatched' || l.status === 'new'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5);
    return needsAction;
  }, []);

  const verticalPerf = useMemo(() => {
    return mockVerticals.map(v => {
      const vLeads = mockLeads.filter(l => l.vertical_id === v.id && !l.deleted_at);
      const vLeadsThisWeek = vLeads.filter(l => {
        const d = new Date(l.created_at);
        const now = new Date();
        const daysDiff = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      });
      const validated = vLeads.filter(l => l.validation_status === 'valid');
      const converted = vLeads.filter(l => l.status === 'signed' || l.status === 'completed');
      const avgQuality =
        vLeads.length > 0 ? vLeads.reduce((sum, l) => sum + (l.quality_score || 0), 0) / vLeads.length : 0;

      return {
        vertical: v.name,
        leads: vLeadsThisWeek.length,
        validatedPct: vLeads.length > 0 ? Math.round((validated.length / vLeads.length) * 100) : 0,
        conversionPct: vLeads.length > 0 ? Math.round((converted.length / vLeads.length) * 100) : 0,
        avgQuality,
      };
    });
  }, []);

  const capUtilization = useMemo(() => {
    return mockOffers
      .filter(o => o.status === 'active')
      .map(o => {
        const partner = mockPartners.find(p => p.id === o.partner_id);
        const vertical = mockVerticals.find(v => v.id === o.vertical_id);
        const caps = o.caps as Record<string, number | null>;
        const monthlyCap = caps.monthly;
        const used = mockPayoutLines.filter(p => p.offer_id === o.id).length;
        const pct = monthlyCap ? Math.round((used / monthlyCap) * 100) : 0;

        return {
          id: o.id,
          name: `${partner?.display_name || o.partner_id} — ${vertical?.name || o.vertical_id}`,
          used,
          max: monthlyCap || 0,
          pct,
          urgency: pct >= 90 ? 'high' : pct >= 70 ? 'medium' : ('low' as const),
        };
      });
  }, []);

  const partnerHealth = useMemo(() => {
    return mockPartners
      .filter(p => p.status === 'active')
      .map(p => {
        const pLeads = mockLeads.filter(l => l.assigned_partner_id === p.id && !l.deleted_at);
        const conversions = pLeads.filter(l => l.status === 'signed' || l.status === 'completed');
        const lastConversion = conversions.sort((a, b) => {
          const at = a.signed_at || a.completed_at || a.created_at;
          const bt = b.signed_at || b.completed_at || b.created_at;
          return new Date(bt).getTime() - new Date(at).getTime();
        })[0];
        const daysSinceConversion = lastConversion
          ? Math.floor(
              (Date.now() - new Date(lastConversion.signed_at || lastConversion.completed_at || lastConversion.created_at).getTime()) /
                (1000 * 60 * 60 * 24)
            )
          : 999;
        const disputeCount = mockDisputes.filter(d => {
          const pl = mockPayoutLines.find(pl => pl.id === d.payout_line_id);
          return pl?.partner_id === p.id;
        }).length;
        const disputeRate = pLeads.length > 0 ? Math.round((disputeCount / pLeads.length) * 100) : 0;

        return {
          id: p.id,
          name: p.display_name,
          daysSinceConversion,
          disputeRate,
          health:
            daysSinceConversion <= 7
              ? 'green'
              : daysSinceConversion <= 14
                ? 'amber'
                : ('red' as const),
        };
      });
  }, []);

  const alerts = useMemo(() => {
    const items = [];
    const unmatchedOld = mockLeads.filter(
      l =>
        l.routing_status === 'unmatched' &&
        !l.deleted_at &&
        new Date(l.created_at).getTime() < Date.now() - 24 * 60 * 60 * 1000
    );
    if (unmatchedOld.length > 0) {
      items.push({
        type: 'warning' as const,
        message: `${unmatchedOld.length} lead${unmatchedOld.length > 1 ? 's' : ''} unmatched >24h — requires manual routing`,
      });
    }
    const highDispute = partnerHealth.filter(p => p.disputeRate > 15);
    highDispute.forEach(p => {
      items.push({
        type: 'danger' as const,
        message: `${p.name} dispute rate ${p.disputeRate}% — above threshold`,
      });
    });
    const nearCap = capUtilization.filter(c => c.pct >= 80 && c.pct < 100);
    nearCap.forEach(c => {
      items.push({
        type: 'info' as const,
        message: `${c.name} approaching cap: ${c.used}/${c.max}`,
      });
    });
    return items;
  }, [partnerHealth, capUtilization]);

  const { loading } = useLoading(stats);

  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const toggleCard = (key: string) => {
    setExpandedCard(prev => prev === key ? null : key);
  };

  return (
    <div className="space-y-10 relative max-w-6xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between pt-2 animate-fade-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            Dashboard
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            Real-time overview of your network
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Nova search trigger */}
          <button
            onClick={() => {
              const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
              window.dispatchEvent(event);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all duration-300 nova-btn"
          >
            <Sparkles size={13} style={{ color: 'var(--accent)' }} />
            <span>Ask Nova</span>
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded border" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>⌘K</span>
          </button>
        </div>
      </div>

      {/* ── Alerts ── */}
      {alerts.length > 0 && (
        <div className="relative space-y-2.5 animate-fade-up stagger-1">
          {alerts.map((alert, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-[13px] transition-all duration-300"
              style={{
                background: alert.type === 'danger' ? 'var(--danger-bg)' : alert.type === 'warning' ? 'var(--warning-bg)' : 'var(--bg-surface-raised)',
                color: alert.type === 'danger' ? 'var(--danger-text)' : alert.type === 'warning' ? 'var(--warning-text)' : 'var(--text-secondary)',
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{
                background: alert.type === 'danger' ? 'var(--danger-dot)' : alert.type === 'warning' ? 'var(--warning-dot)' : 'var(--text-muted)',
              }} />
              <span className="font-medium">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Inbox / Actionable Leads — Card Feed ── */}
      <div className="animate-fade-up stagger-2 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox size={14} strokeWidth={1.5} style={{ color: 'var(--text-muted)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Inbox</span>
            {inboxLeads.length > 0 && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}
              >
                {inboxLeads.length}
              </span>
            )}
          </div>
          <Link
            to="/leads"
            className="flex items-center gap-1 text-[11px] transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            View all
            <ChevronRight size={12} />
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl p-5 border animate-pulse" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bg-input)' }} />
                  <div className="flex-1 space-y-2">
                    <div className="w-32 h-3 rounded" style={{ background: 'var(--bg-input)' }} />
                    <div className="w-48 h-2.5 rounded" style={{ background: 'var(--bg-input)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : inboxLeads.length > 0 ? (
          inboxLeads.map(lead => {
            const vertical = mockVerticals.find(v => v.id === lead.vertical_id);
            const geo = mockGeos.find(g => g.id === lead.geo_id);
            const partner = mockPartners.find(p => p.id === lead.assigned_partner_id);
            const offer = mockOffers.find(o => o.id === lead.assigned_offer_id);
            const isExpanded = expandedCard === lead.id;

            const detailPanel = (
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Vertical', value: vertical?.name || lead.vertical_id },
                    { label: 'Geo', value: geo?.name || lead.geo_id },
                    { label: 'Status', value: lead.status },
                    { label: 'Routing', value: lead.routing_status },
                  ].map(item => (
                    <div key={item.label} className="rounded-lg p-2.5 border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                      <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Phone size={12} />
                      {lead.phone}
                    </a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors"
                      style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                    >
                      <Mail size={12} />
                      {lead.email}
                    </a>
                  )}
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                  >
                    <MapPin size={12} />
                    {lead.city}, {lead.postcode}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-separator)', background: 'var(--bg-input)' }}
                  >
                    <Calendar size={12} />
                    {formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  >
                    <ArrowUpRight size={12} />
                    View Lead
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border transition-colors"
                    style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)', background: 'var(--bg-input)' }}
                  >
                    <Route size={12} />
                    Re-route
                  </button>
                  {lead.fraud_signals.length > 0 && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border"
                      style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                    >
                      <AlertTriangle size={12} />
                      {lead.fraud_signals.length} fraud signal{lead.fraud_signals.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            );

            return (
              <BentoCard
                key={lead.id}
                size="full"
                expandable
                expanded={isExpanded}
                onToggle={() => toggleCard(lead.id)}
                active={isExpanded}
                className="row-hover"
                header={
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: lead.routing_status === 'unmatched'
                          ? 'var(--danger-dot)'
                          : lead.status === 'new'
                            ? 'var(--warning-dot)'
                            : 'var(--success-dot)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {lead.firstname} {lead.lastname}
                        </h3>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{geo?.name || lead.geo_id}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize"
                          style={{
                            borderColor: lead.routing_status === 'unmatched' ? 'var(--danger-border)' : 'var(--border-primary)',
                            background: lead.routing_status === 'unmatched' ? 'var(--danger-bg)' : 'var(--bg-surface-raised)',
                            color: lead.routing_status === 'unmatched' ? 'var(--danger-text)' : 'var(--text-tertiary)',
                          }}
                        >
                          {lead.routing_status === 'unmatched' ? 'Needs routing' : lead.status}
                        </span>
                        {lead.fraud_signals.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                            style={{ color: 'var(--danger-text)', borderColor: 'var(--danger-border)', background: 'var(--danger-bg)' }}
                          >
                            Fraud alert
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {lead.phone && (
                          <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            <Phone size={10} />
                            {lead.phone}
                          </span>
                        )}
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{vertical?.name || lead.vertical_id}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{partner?.display_name || 'Unmatched'}</span>
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs font-semibold"
                        style={{
                          color: (lead.quality_score || 0) >= 0.7 ? 'var(--text-primary)' :
                            (lead.quality_score || 0) >= 0.4 ? 'var(--warning-text)' : 'var(--danger-text)',
                        }}
                      >
                        {(() => {
                          const s = lead.quality_score;
                          if (!s) return '—';
                          if (s >= 0.7) return 'High';
                          if (s >= 0.4) return 'Medium';
                          return 'Low';
                        })()}
                      </span>
                    </div>
                  </div>
                }
                footer={detailPanel}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-md border"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                  >
                    {vertical?.name || lead.vertical_id}
                  </span>
                  {partner ? (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{partner.display_name}</span>
                  ) : (
                    <span className="text-xs font-medium" style={{ color: 'var(--danger-text)' }}>Unmatched</span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{offer?.payout_model?.toUpperCase() || '—'}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDistanceToNow(new Date(lead.created_at), { addSuffix: false })} ago</span>
                  {lead.city && (
                    <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <MapPin size={10} />
                      {lead.city}, {lead.postcode}
                    </span>
                  )}
                </div>
              </BentoCard>
            );
          })
        ) : (
          <BentoCard size="full">
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <CheckCircle size={18} strokeWidth={1.5} style={{ color: 'var(--success-text)' }} />
              <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>All leads are handled. Nothing to review.</span>
            </div>
          </BentoCard>
        )}
      </div>

      {/* ── Stats Grid — 6 tiles, borderless artifact style ── */}
      {loading ? (
        <div className="bento-grid animate-fade-up stagger-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonStat key={i} />
          ))}
        </div>
      ) : (
        <div className="bento-grid animate-fade-up stagger-3">
          {stats.map((tile, i) => {
            const key = tile.label;
            const isExpanded = expandedCard === key;

            const detailContent = (() => {
              if (key === 'Leads this month') {
                const topVerticals = mockVerticals.map(v => ({
                  name: v.name,
                  count: mockLeads.filter(l => {
                    const d = new Date(l.created_at);
                    return d.getMonth() === new Date().getMonth() && l.vertical_id === v.id;
                  }).length,
                })).sort((a, b) => b.count - a.count).slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Top Verticals</p>
                    {topVerticals.map(v => (
                      <div key={v.name} className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{v.name}</span>
                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{v.count}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (key === 'Validated rate') {
                const invalid = mockLeads.filter(l => l.validation_status === 'invalid' && !l.deleted_at).slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Recent Invalid</p>
                    {invalid.map(l => (
                      <div key={l.id} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{l.firstname} {l.lastname}</span>
                        <span className="text-[10px]" style={{ color: 'var(--danger-text)' }}>{l.routing_status}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (key === 'Routed rate') {
                const unmatched = mockLeads.filter(l => l.routing_status === 'unmatched' && !l.deleted_at).slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Unmatched Leads</p>
                    {unmatched.map(l => (
                      <div key={l.id} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{l.firstname} {l.lastname}</span>
                        <span className="text-[10px]" style={{ color: 'var(--danger-text)' }}>{formatDistanceToNow(new Date(l.created_at))} ago</span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (key === 'Leads signed') {
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Conversion Path</p>
                    <div className="flex items-center gap-2">
                      {['new', 'contacted', 'signed'].map((s, i) => (
                        <Fragment key={s}>
                          <span className="text-[10px] px-2 py-0.5 rounded border capitalize"
                            style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                          >{s}</span>
                          {i < 2 && <span style={{ color: 'var(--text-muted)' }}>→</span>}
                        </Fragment>
                      ))}
                    </div>
                    <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Avg time to sign: 4.2 days</p>
                  </div>
                );
              }
              if (key === 'Gross payout') {
                const topPartners = mockPartners.map(p => {
                  const pLines = mockPayoutLines.filter(pl => {
                    const d = new Date(pl.created_at);
                    return d.getMonth() === new Date().getMonth() && pl.partner_id === p.id;
                  });
                  return { name: p.display_name, amount: pLines.reduce((s, pl) => s + pl.amount_eur, 0) };
                }).sort((a, b) => b.amount - a.amount).slice(0, 3);
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Top Partners</p>
                    {topPartners.map(p => (
                      <div key={p.name} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{p.name}</span>
                        <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>€{p.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              if (key === 'Unmatched') {
                const oldUnmatched = mockLeads.filter(
                  l => l.routing_status === 'unmatched' && !l.deleted_at && new Date(l.created_at).getTime() < Date.now() - 24 * 60 * 60 * 1000
                );
                return (
                  <div className="space-y-2 pt-2">
                    <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Needs Routing</p>
                    {oldUnmatched.slice(0, 3).map(l => (
                      <div key={l.id} className="flex items-center justify-between">
                        <span className="text-xs truncate max-w-[160px]" style={{ color: 'var(--text-secondary)' }}>{l.firstname} {l.lastname}</span>
                        <Link to={`/leads/${l.id}`} className="text-[10px] hover:underline" style={{ color: 'var(--accent)' }}>Open</Link>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            })();

            return (
              <BentoCard
                key={i}
                size="sm"
                glow={false}
                active={isExpanded}
                expandable={!!detailContent}
                expanded={isExpanded}
                onToggle={() => toggleCard(key)}
                header={
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-muted)' }}
                    >
                      {tile.icon}
                    </div>
                    <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{tile.label}</span>
                  </div>
                }
                footer={detailContent || undefined}
              >
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{tile.value}</p>
                    <div className="flex items-center gap-1 mt-2 text-[11px] font-medium"
                      style={{ color: tile.deltaPositive ? 'var(--accent)' : 'var(--danger-text)' }}
                    >
                      {tile.deltaPositive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                      <span>{tile.delta}</span>
                    </div>
                  </div>
                </div>
              </BentoCard>
            );
          })}
        </div>
      )}

      {/* ── Detail Panels — 3 columns ── */}
      <div className="bento-grid animate-fade-up stagger-4">
        {/* Vertical Performance */}
        <BentoCard
          size="md"
          header={
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Vertical Performance</span>
              <Activity size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
          }
        >
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-24 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  <div className="flex-1 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  <div className="w-8 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="card-feed">
              <div className="card-feed-item py-2 flex items-center justify-between text-[10px] font-medium uppercase tracking-wider"
                style={{ color: 'var(--text-muted)' }}
              >
                <span className="flex-1">Vertical</span>
                <span className="w-12 text-right">Leads</span>
                <span className="w-12 text-right">Valid</span>
                <span className="w-12 text-right">Conv</span>
                <span className="w-12 text-right">Qual</span>
              </div>
              {verticalPerf.map((v, i) => (
                <div key={i} className="card-feed-item flex items-center justify-between py-3 cursor-pointer"
                onClick={() => navigate('/leads')}
              >
                  <span className="flex-1 text-[13px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{v.vertical}</span>
                  <span className="w-12 text-right text-[13px]" style={{ color: 'var(--text-tertiary)' }}>{v.leads}</span>
                  <span className="w-12 text-right text-[13px] font-medium"
                    style={{ color: v.validatedPct >= 80 ? 'var(--success-text)' : v.validatedPct >= 50 ? 'var(--warning-text)' : 'var(--danger-text)' }}
                  >{v.validatedPct}%</span>
                  <span className="w-12 text-right text-[13px] font-medium"
                    style={{ color: v.conversionPct >= 20 ? 'var(--text-secondary)' : v.conversionPct >= 10 ? 'var(--warning-text)' : 'var(--text-muted)' }}
                  >{v.conversionPct}%</span>
                  <span className="w-12 text-right text-[13px] font-medium flex items-center gap-1.5 justify-end"
                    style={{ color: v.avgQuality >= 0.7 ? 'var(--success-text)' : v.avgQuality >= 0.4 ? 'var(--warning-text)' : 'var(--danger-text)' }}
                  >
                    <span className="w-1 h-1 rounded-full" style={{ background: 'var(--accent)' }} />
                    {v.avgQuality.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </BentoCard>

        {/* Cap Utilization */}
        <BentoCard
          size="md"
          header={
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Cap Utilization</span>
              <Activity size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
          }
        >
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-2">
                    <div className="w-32 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                    <div className="w-16 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  </div>
                  <div className="w-full h-[3px] rounded-full" style={{ background: 'var(--bg-input)' }} />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3.5">
              {capUtilization.map(cap => (
                <div key={cap.id} className="group">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[13px] font-medium truncate max-w-[55%]" style={{ color: 'var(--text-secondary)' }}>{cap.name}</span>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border"
                      style={{
                        borderColor: cap.urgency === 'high' ? 'var(--danger-border)' : cap.urgency === 'medium' ? 'var(--warning-border)' : 'var(--border-primary)',
                        background: cap.urgency === 'high' ? 'var(--danger-bg)' : cap.urgency === 'medium' ? 'var(--warning-bg)' : 'var(--bg-surface-raised)',
                        color: cap.urgency === 'high' ? 'var(--danger-text)' : cap.urgency === 'medium' ? 'var(--warning-text)' : 'var(--text-tertiary)',
                      }}
                    >{cap.used}/{cap.max}</span>
                  </div>
                  <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(cap.pct, 100)}%`,
                        background: cap.pct >= 90 ? 'var(--danger-dot)' : cap.pct >= 70 ? 'var(--warning-dot)' : 'var(--accent)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </BentoCard>

        {/* Partner Health */}
        <BentoCard
          size="md"
          header={
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Partner Health</span>
              <Users size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
          }
        >
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  <div className="flex-1 space-y-1.5">
                    <div className="w-32 h-3 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                    <div className="w-20 h-2.5 rounded animate-pulse" style={{ background: 'var(--bg-input)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {partnerHealth.map(p => (
                <div key={p.id}
                  className="flex items-center gap-3 py-2.5 group cursor-pointer rounded-lg transition-colors hover-surface-raised"
                  onClick={() => navigate(`/partners/${p.id}`)}
                >
                  <div className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      background: p.health === 'green' ? 'var(--success-dot)' : p.health === 'amber' ? 'var(--warning-dot)' : 'var(--danger-dot)',
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{p.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                        <Clock size={10} strokeWidth={1.5} />
                        {p.daysSinceConversion <= 999 ? `${p.daysSinceConversion}d` : 'No conv'}
                      </span>
                      <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.disputeRate}% disputes</span>
                    </div>
                  </div>
                  <ArrowUpRight size={14} className="transition-colors shrink-0" style={{ color: 'var(--text-muted)' }} />
                </div>
              ))}
            </div>
          )}
        </BentoCard>
      </div>
    </div>
  );
}
